/* ==========================================================================
   Services / capability areas.

   STRUCTURE — five services, confirmed by the client.

   This mirrors how the source documents are actually organised. The four
   AI/quantum areas are not separate services: in Website-Updates.docx they sit
   underneath the heading "AI Governance & Quantum-Ready Cybersecurity" as its
   "Our Capabilities" list. They are nested here the same way.

     1. AI Governance & Quantum-Ready Cybersecurity   (Website-Updates.docx)
          • Cybersecurity & Digital Resilience
          • AI Governance & Risk Management
          • AI Security & Trusted AI
          • Post-Quantum Cryptography & Quantum Readiness
     2. Cybersecurity Solutions                       (Bethel Content1.docx)
     3. Change Management and IT Service Management    (Bethel Content1.docx)
     4. Enterprise Services                           (Bethel Content1.docx)
     5. LscNexus Transportation & Mobility Solutions  → /lscnexus

   LscNexus keeps its own page and data file (data/lscnexus.js); it appears in
   the services menu as the fifth item.
   ========================================================================== */

import { lscnexus } from './lscnexus';

export const capabilities = [
  /* ---------------------------------------------------------------- 1 */
  {
    slug: 'ai-governance-quantum-ready-cybersecurity',
    title: 'AI Governance & Quantum-Ready Cybersecurity',
    navTitle: 'AI Governance & Quantum-Ready Cybersecurity',
    eyebrow: 'Service',
    summary:
      'AI governance, AI security, post-quantum readiness and cyber resilience delivered as one mission-focused strategy rather than four separate initiatives.',
    lead:
      'Bethel Innovations helps organizations adopt AI securely, govern emerging technologies responsibly, and prepare for the quantum era. We integrate AI governance, cybersecurity engineering, DevSecOps, data protection and Post-Quantum Cryptography (PQC) into a unified approach to trusted digital transformation.',
    body: [
      'These four areas are deliberately delivered together. Governance without engineering produces policy nobody implements; engineering without governance produces controls nobody can evidence. Treating them as one programme is what makes the roadmap defensible to an auditor and achievable for a delivery team.',
    ],
    /* The four capability areas from Website-Updates.docx, verbatim */
    groups: [
      {
        heading: 'Cybersecurity & Digital Resilience',
        items: [
          'Enterprise cybersecurity architecture',
          'Governance, Risk, and Compliance (GRC)',
          'Zero Trust architecture',
          'Security operations and continuous monitoring',
          'Vulnerability and risk management',
          'Secure cloud and application modernization',
          'Cyber resilience and incident response',
        ],
      },
      {
        heading: 'AI Governance & Risk Management',
        items: [
          'AI governance frameworks, policies, roles, and accountability',
          'AI risk assessments and lifecycle governance',
          'Model validation, performance monitoring, and bias testing',
          'AI impact assessments and responsible AI practices',
          'AI explainability, transparency, and auditability',
          'AI privacy, data protection, and third-party risk management',
          'Governance for generative AI and emerging agentic AI technologies',
        ],
      },
      {
        heading: 'AI Security & Trusted AI',
        items: [
          'Secure AI architecture and implementation',
          'Protection against model manipulation, poisoning, and unauthorized extraction',
          'AI application and data security',
          'Identity, access, and Zero Trust controls for AI environments',
          'Continuous AI security monitoring and incident response',
          'DevSecOps integration for secure AI development and deployment',
        ],
      },
      {
        heading: 'Post-Quantum Cryptography & Quantum Readiness',
        items: [
          'PQC strategy and enterprise migration planning',
          'Cryptographic discovery and inventory',
          'Cryptographic risk and dependency assessments',
          'Crypto-agility strategy and architecture',
          'PQC migration roadmaps and implementation planning',
          'High Value Asset (HVA) and sensitive-data protection',
          'Quantum-readiness assessments and transition planning',
        ],
      },
    ],
    image: null,
  },

  /* ---------------------------------------------------------------- 2 */
  {
    slug: 'cybersecurity-solutions',
    title: 'Cybersecurity Solutions',
    navTitle: 'Cybersecurity Solutions',
    eyebrow: 'Service',
    summary:
      'Full-cycle Security Assessment & Authorization and automated control validation, built on the frameworks federal programs are measured against.',
    lead:
      'We lead cybersecurity activities including full-cycle Security Assessments & Authorizations (A&A) based on frameworks such as FISMA, FedRAMP and the NIST Risk Management Framework, and the development of System Security Plans, IT policies and procedures, contingency plans, configuration management, incident response plans and supporting security artifacts.',
    body: [
      'Our cybersecurity experts understand how to stay proactive in the cyber battle — finding protection failures before an adversary does and closing the security gap before it can be exploited. We help customers implement automated security testing to continuously validate their security controls, their processes and their people.',
      'We continuously deliver metrics on readiness against common attack vectors including Credential Access, Exfiltration, and Command & Control, so leadership sees posture as a measurable trend rather than a point-in-time assessment.',
    ],
    groups: [
      {
        heading: 'Assessment & authorization',
        items: [
          'Full-cycle Security Assessment & Authorization (A&A)',
          'System Security Plans and security artifacts',
          'IT policies and procedures',
          'Contingency and incident response planning',
          'Configuration management',
          'Mapping program requirements to NIST SP 800-53 controls',
          'FISMA, OMB and US-CERT compliance support',
        ],
      },
      {
        heading: 'Standards & platforms',
        items: [
          'ISO 27001 / 27002',
          'NIST SP 800-53 and NIST CSF',
          'FISMA and FedRAMP',
          'PCI-DSS',
          'AWS and GCP VPC management',
          'Application security implementation and management',
        ],
      },
      {
        heading: 'Continuous validation',
        items: [
          'Automated security testing of controls, processes and people',
          'Readiness metrics for Credential Access',
          'Readiness metrics for Exfiltration',
          'Readiness metrics for Command & Control',
        ],
      },
    ],
    image: null,
  },

  /* ---------------------------------------------------------------- 3 */
  {
    slug: 'change-and-it-service-management',
    title: 'Change Management and IT Service Management',
    navTitle: 'Change Management and IT Service Management',
    eyebrow: 'Service',
    summary:
      'Tactical mission support, IT audit assessment, and service management that turns technology into measurable operational performance.',
    lead:
      'We provide tactical mission support, IT audit assessment and critical infrastructure support. We dedicate our top professionals, industry intelligence and systems expertise to making organizations more customer-centric, competitive and productive.',
    body: [
      'We strategically help our clients leverage technology as an enabler for unlocking business value and optimizing operations, with a portfolio of IT service management delivered through effective use of industry-standard tools.',
    ],
    groups: [
      {
        heading: 'Service areas',
        items: [
          'Tactical mission support',
          'IT audit and assessment',
          'Critical infrastructure support',
          'IT governance and IT service management',
          'Change and configuration management',
          'Information assurance',
          'DevOps and CI/CD enablement',
        ],
      },
    ],
    image: null,
  },

  /* ---------------------------------------------------------------- 4 */
  {
    slug: 'enterprise-services',
    title: 'Enterprise Services',
    navTitle: 'Enterprise Services',
    eyebrow: 'Service',
    summary:
      'Deep technology expertise, architecture capability and project management applied to large-scale, complex delivery.',
    lead:
      'We provide solutions to challenging IT problems by combining deep technology expertise, architecture capabilities and extensive project management skill.',
    body: [
      'Our team has significant experience delivering large-scale and complex solutions using delivery processes that drive efficiency in product development, legacy modernization, business IT planning and technical architecture definition.',
      'We support application integration, testing and production deployment activities for application development, infrastructure engineering, and the integration of IRS applications and portal environments.',
    ],
    groups: [
      {
        heading: 'Delivery focus',
        items: [
          'Product development',
          'Legacy modernization',
          'Business IT planning',
          'Technical architecture definition',
          'Application integration, testing and production deployment',
          'Infrastructure engineering and integration',
          'Systems integration and data integration',
        ],
      },
    ],
    image: null,
  },
];

/* --------------------------------------------------------------------------
   THE FIVE SERVICES — one list, so the count cannot drift.

   The four capability pages above plus LscNexus, which keeps its own page and
   data file but is the fifth service. Anything that presents "the services" —
   the home page, the capabilities hub, the capability statement, the position
   counter on a detail page — reads this list rather than counting the four.
   The nav menu in data/navigation.js is the same five in the same order.
   -------------------------------------------------------------------------- */

export const services = [
  ...capabilities.map((c) => ({
    slug: c.slug,
    title: c.navTitle,
    summary: c.summary,
    to: `/capabilities/${c.slug}`,
  })),
  {
    slug: 'lscnexus',
    title: `${lscnexus.name} ${lscnexus.title}`,
    summary:
      'Transportation planning, traffic engineering, roadway safety, GIS and data analytics for agencies and communities.',
    to: '/lscnexus',
  },
];

/* --------------------------------------------------------------------------
   The integrated-approach narrative and deliverables list are shared across
   the services hub and the capability statement.
   Source: Website-Updates.docx
   -------------------------------------------------------------------------- */

export const integratedApproach = {
  heading: 'One strategy, not four initiatives',
  body: [
    'Bethel Innovations differentiates itself by connecting AI governance, cybersecurity, DevSecOps and quantum readiness into a single mission-focused strategy.',
    'Rather than treating emerging technology risks as isolated initiatives, we help organizations establish the governance, security controls, architecture and implementation roadmaps needed to innovate securely and responsibly.',
  ],
  closing:
    'Bethel Innovations enables organizations to innovate with confidence — governing AI responsibly, strengthening cyber resilience, and preparing critical systems and data for a quantum-secure future.',
};

export const deliverables = [
  'AI Governance Frameworks & Operating Models',
  'AI Risk & Impact Assessments',
  'Trusted AI & Responsible AI Strategies',
  'AI Security Assessments',
  'Model Validation & Performance Assessments',
  'AI Explainability & Auditability Assessments',
  'PQC Readiness & Migration Roadmaps',
  'Cryptographic Inventories & Risk Assessments',
  'Crypto-Agility Strategies',
  'HVA Protection Strategies',
  'Zero Trust & Secure Architecture',
  'DevSecOps Implementation',
  'Cybersecurity & GRC Assessments',
  'Executive Roadmaps and Implementation Plans',
];

/* Slugs that existed while the four AI/quantum areas were briefly separate
   pages. Kept so any link already shared resolves to the service that now
   contains them, rather than 404ing or bouncing to the hub. */
const SLUG_ALIASES = {
  'cybersecurity-digital-resilience': 'ai-governance-quantum-ready-cybersecurity',
  'ai-governance-risk-management': 'ai-governance-quantum-ready-cybersecurity',
  'ai-security-trusted-ai': 'ai-governance-quantum-ready-cybersecurity',
  'post-quantum-readiness': 'ai-governance-quantum-ready-cybersecurity',
};

export const resolveSlug = (slug) => SLUG_ALIASES[slug] || slug;

export const getCapability = (slug) =>
  capabilities.find((c) => c.slug === resolveSlug(slug));
